import React from 'react';
import Icon from '../../../components/AppIcon';


const StorePreview = ({ storeData, viewMode = 'desktop' }) => {
  const formatBusinessHours = (hours) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    return days?.map((day, index) => {
      const dayHours = hours?.[day];
      if (dayHours?.closed) {
        return `${dayNames?.[index]}: Closed`;
      }
      return `${dayNames?.[index]}: ${dayHours?.open || '09:00'} - ${dayHours?.close || '17:00'}`;
    });
  };

  const socialLinks = Object.entries(storeData?.socialMedia || {})?.filter(([_, url]) => url && url?.trim())?.map(([platform, url]) => ({ platform, url }));

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle h-full">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground flex items-center space-x-2">
            <Icon name="Eye" size={20} />
            <span>Live Preview</span>
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {}}
              className={`p-2 rounded-md transition-micro ${
                viewMode === 'desktop' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted-foreground hover:text-muted'
              }`}
            >
              <Icon name="Monitor" size={16} />
            </button>
            <button
              onClick={() => {}}
              className={`p-2 rounded-md transition-micro ${
                viewMode === 'mobile' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted-foreground hover:text-muted'
              }`}
            >
              <Icon name="Smartphone" size={16} />
            </button>
          </div>
        </div>
      </div>
      <div className="p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
        <div className={`mx-auto ${viewMode === 'mobile' ? 'max-w-sm' : 'max-w-2xl'}`}>
          {/* Store Header */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Store" size={32} className="text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {storeData?.name || 'Your Store Name'}
            </h1>
            <p className="text-muted-foreground mb-4">
              {storeData?.description || 'Your business description will appear here'}
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Icon name="MapPin" size={16} />
                <span>{storeData?.category || 'Business Category'}</span>
              </span>
              <span className="flex items-center space-x-1">
                <Icon name="Clock" size={16} />
                <span>Open Now</span>
              </span>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-muted/50 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Phone" size={20} />
              <span>Contact Information</span>
            </h2>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Mail" size={16} className="text-muted-foreground" />
                <span className="text-foreground">{storeData?.email || 'email@example.com'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Phone" size={16} className="text-muted-foreground" />
                <span className="text-foreground">{storeData?.phone || '+1 (555) 123-4567'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <span className="text-foreground">{storeData?.address || '123 Main Street, City, State'}</span>
              </div>
              {storeData?.website && (
                <div className="flex items-center space-x-3">
                  <Icon name="Globe" size={16} className="text-muted-foreground" />
                  <a href={storeData?.website} className="text-primary hover:underline">
                    {storeData?.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Business Hours */}
          <div className="bg-muted/50 rounded-lg p-6 mb-6">
       
            <div className="space-y-2">
              {formatBusinessHours(storeData?.businessHours || {})?.map((hours, index) => (
                <div key={index} className="flex justify-between items-center py-1">
                  <span className="text-foreground font-medium">{hours?.split(':')?.[0]}:</span>
                  <span className="text-muted-foreground">{hours?.split(':')?.slice(1)?.join(':')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Map Integration */}
          <div className="bg-muted/50 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Map" size={20} />
              <span>Location</span>
            </h2>
            <div className="w-full h-64 bg-muted rounded-lg overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Store Location"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=40.7128,-74.0060&z=14&output=embed"
                className="border-0"
              />
            </div>
          </div>

          {/* Social Media Links */}
          {socialLinks?.length > 0 && (
            <div className="bg-muted/50 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center space-x-2">
                <Icon name="Share2" size={20} />
                <span>Follow Us</span>
              </h2>
              <div className="flex flex-wrap gap-3">
                {socialLinks?.map(({ platform, url }) => (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-micro"
                  >
                    <Icon 
                      name={platform === 'facebook' ? 'Facebook' : 
                            platform === 'instagram' ? 'Instagram' :
                            platform === 'twitter' ? 'Twitter' : 'Linkedin'} 
                      size={16} 
                    />
                    <span className="capitalize">{platform}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StorePreview;